import { CommonModule, formatDate } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { MenuItem, MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { PageDataPanelComponent } from '../../../shared/page-layout/page-data-panel.component';
import { PageHeroComponent } from '../../../shared/page-layout/page-hero.component';
import { PageMetricStripComponent } from '../../../shared/page-layout/page-metric-strip.component';
import { PageMetric } from '../../../shared/page-layout/page-layout.models';
import { PageSearchPanelComponent } from '../../../shared/page-layout/page-search-panel.component';
import { PageShellComponent } from '../../../shared/page-layout/page-shell.component';
import { SessionStorageService } from '../../../services/session-storage.service';
import { FLEET_ACTIVE_OPTIONS, FleetTrailerType } from '../models/trailer.model';
import { FleetTrailerService } from '../trailer.service';

@Component({
    selector: 'app-trailer-type-list',
    standalone: true,
    templateUrl: './trailer-type-list.component.html',
    styleUrls: ['../trailer-page.css'],
    imports: [
        CommonModule,
        FormsModule,
        RouterModule,
        BreadcrumbModule,
        ButtonModule,
        InputTextModule,
        SelectModule,
        TableModule,
        TagModule,
        ToastModule,
        PageShellComponent,
        PageHeroComponent,
        PageSearchPanelComponent,
        PageMetricStripComponent,
        PageDataPanelComponent
    ]
})
export class TrailerTypeListComponent implements OnInit {
    breadcrumbItems: MenuItem[] = [];
    homeBreadcrumb: MenuItem = { icon: 'pi pi-home', routerLink: '/' };
    rows: FleetTrailerType[] = [];
    isLoading = false;
    totalRecords = 0;
    first = 0;
    rowsPerPage = 10;
    keyword = '';
    active: boolean | null = null;
    readonly activeOptions = FLEET_ACTIVE_OPTIONS;
    private updating = new Set<number>();

    constructor(
        private trailerService: FleetTrailerService,
        private sessionStorageService: SessionStorageService,
        private messageService: MessageService,
        private router: Router
    ) {}

    ngOnInit(): void {
        this.breadcrumbItems = [{ label: 'Fleet', routerLink: '/fleet' }, { label: 'Trailer Types' }];
    }

    loadTrailerTypes(event?: TableLazyLoadEvent): void {
        if (event) {
            this.first = event.first ?? 0;
            this.rowsPerPage = event.rows ?? 10;
        }

        const companyId = this.companyId();
        if (!companyId) {
            this.messageService.add({ severity: 'error', summary: 'Company missing', detail: 'No owning company is available in session.' });
            return;
        }

        this.isLoading = true;
        this.trailerService.searchTrailerTypes({
            owningCompanyId: companyId,
            page: Math.floor(this.first / this.rowsPerPage),
            pageSize: this.rowsPerPage,
            keyword: this.keyword,
            active: this.active
        }).subscribe({
            next: response => {
                this.rows = response.items ?? [];
                this.totalRecords = response.count ?? this.rows.length;
                this.isLoading = false;
            },
            error: () => {
                this.isLoading = false;
                this.messageService.add({ severity: 'error', summary: 'Load failed', detail: 'Could not load trailer types.' });
            }
        });
    }

    search(): void {
        this.first = 0;
        this.loadTrailerTypes();
    }

    clearSearch(): void {
        this.keyword = '';
        this.active = null;
        this.search();
    }

    create(): void {
        this.router.navigate(['/fleet/trailer-type/create']);
    }

    view(row: FleetTrailerType): void {
        if (row.trailerTypeId) {
            this.router.navigate(['/fleet/trailer-type/view', row.trailerTypeId]);
        }
    }

    edit(row: FleetTrailerType): void {
        if (row.trailerTypeId) {
            this.router.navigate(['/fleet/trailer-type/edit', row.trailerTypeId]);
        }
    }

    updateActive(row: FleetTrailerType, active: boolean): void {
        if (!row.trailerTypeId || row.active === active) {
            return;
        }

        const previous = row.active !== false;
        row.active = active;
        this.updating.add(row.trailerTypeId);
        this.trailerService.updateTrailerTypeActive(row.trailerTypeId, active).subscribe({
            next: response => {
                row.active = response.data?.active ?? active;
                this.updating.delete(row.trailerTypeId!);
                this.messageService.add({ severity: 'success', summary: 'Updated', detail: `Trailer type marked ${active ? 'active' : 'inactive'}.` });
            },
            error: () => {
                row.active = previous;
                this.updating.delete(row.trailerTypeId!);
                this.messageService.add({ severity: 'error', summary: 'Update failed', detail: 'Could not update trailer type status.' });
            }
        });
    }

    metrics(): PageMetric[] {
        return [
            { label: 'Trailer Types', value: this.totalRecords, hint: 'Matching type records', tone: 'info' },
            { label: 'Loaded', value: this.rows.length, hint: 'Rows in current page', tone: 'default' },
            { label: 'Active', value: this.rows.filter(row => row.active !== false).length, hint: 'Visible active rows', tone: 'success' },
            { label: 'Linked Trailers', value: this.rows.reduce((sum, row) => sum + (row.activeTrailerCount ?? 0), 0), hint: 'Visible active trailer links', tone: 'warn' }
        ];
    }

    activeFilterCount(): number {
        return [this.keyword, this.active === null ? '' : 'active'].filter(value => value?.toString().trim()).length;
    }

    activeSeverity(row: FleetTrailerType): 'success' | 'danger' {
        return row.active === false ? 'danger' : 'success';
    }

    isUpdating(row: FleetTrailerType): boolean {
        return !!row.trailerTypeId && this.updating.has(row.trailerTypeId);
    }

    dateLabel(value?: string | Date): string {
        return value ? formatDate(value, 'MMM d, y', 'en-US') : '-';
    }

    private companyId(): number {
        return this.sessionStorageService.getCompany()?.id ?? 0;
    }
}
